import React, { useState, useRef } from 'react';
import Button from './Button';
import Icon from '../AppIcon';

const FileUpload = ({ 
  label, 
  accept = "image/*", 
  multiple = false, 
  onFileSelect, 
  disabled = false,
  error = null,
  className = "",
  description = null,
  maxFileSize = 5, // MB
  allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
  currentFileName = null,
  uploadProgress = null,
  isUploading = false
}) => {
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (files) => {
    if (!files || files?.length === 0) return;

    const fileArray = Array.from(files);
    
    // Validate files
    const validatedFiles = fileArray?.filter(file => {
      // Check file type
      if (!allowedTypes?.includes(file?.type?.toLowerCase())) {
        return false;
      }
      
      // Check file size
      const maxSizeBytes = maxFileSize * 1024 * 1024;
      if (file?.size > maxSizeBytes) {
        return false;
      }
      
      return true;
    });

    if (validatedFiles?.length > 0) {
      onFileSelect?.(multiple ? validatedFiles : validatedFiles?.[0]);
    }
  };

  const handleInputChange = (e) => {
    handleFileSelect(e?.target?.files);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    if (!disabled && !isUploading) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    
    if (!disabled && !isUploading) {
      handleFileSelect(e?.dataTransfer?.files);
    }
  };

  const handleButtonClick = () => {
    if (!disabled && !isUploading) {
      fileInputRef?.current?.click();
    }
  };

  const formatFileTypes = () => {
    const typeMap = {
      'image/jpeg': 'JPG',
      'image/jpg': 'JPG', 
      'image/png': 'PNG',
      'image/gif': 'GIF',
      'image/webp': 'WebP',
      'image/svg+xml': 'SVG'
    };
    
    return allowedTypes?.map(type => typeMap?.[type] || type)?.join(', ');
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-text-primary">
          {label}
        </label>
      )}

      <div
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200
          ${dragOver ? 'border-primary bg-primary/5' : 'border-border'}
          ${disabled || isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary hover:bg-primary/5'}
          ${error ? 'border-destructive bg-destructive/5' : ''}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleButtonClick}
      >
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleInputChange}
          disabled={disabled || isUploading}
          className="hidden"
        />

        {/* Upload UI */}
        <div className="space-y-4">
          {isUploading ? (
            <>
              <Icon name="Loader2" size={32} className="mx-auto text-primary animate-spin" />
              <div className="text-sm font-medium text-text-primary">
                Uploading...
              </div>
              {uploadProgress !== null && (
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              )}
            </>
          ) : (
            <>
              <Icon 
                name={currentFileName ? "CheckCircle" : "Upload"} 
                size={32} 
                className={`mx-auto ${currentFileName ? 'text-success' : 'text-muted-foreground'}`} 
              />
              
              <div className="space-y-2">
                <div className="text-sm font-medium text-text-primary">
                  {currentFileName ? (
                    <span className="text-success">
                      Current: {currentFileName}
                    </span>
                  ) : (
                    'Drop files here or click to browse'
                  )}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  disabled={disabled || isUploading}
                  type="button"
                >
                  {currentFileName ? 'Change File' : 'Choose File'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* File requirements */}
      <div className="text-xs text-text-secondary space-y-1">
        <div>Accepted formats: {formatFileTypes()}</div>
        <div>Maximum file size: {maxFileSize}MB</div>
        {description && <div>{description}</div>}
      </div>

      {/* Error display */}
      {error && (
        <div className="flex items-center space-x-2 text-destructive text-sm">
          <Icon name="AlertCircle" size={16} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default FileUpload;